import { create } from 'zustand';
import { api, setAccessToken, setOrganizationId } from '@/lib/api';

/**
 * State sesi.
 *
 * Access token sengaja **hanya** disimpan di memori, tidak di localStorage.
 * Token di localStorage bisa dicuri lewat XSS; yang di memori hilang saat tab
 * ditutup. Sesi tetap bertahan antar-kunjungan lewat cookie httpOnly berisi
 * refresh token, yang tidak bisa dibaca JavaScript sama sekali.
 */
export const useAuthStore = create((set, get) => ({
  user: null,
  organization: null,
  organizations: [],
  role: null,
  status: 'idle', // idle | loading | authenticated | unauthenticated

  isReady: () => get().status === 'authenticated' || get().status === 'unauthenticated',

  applySession(payload) {
    setAccessToken(payload.accessToken ?? null);
    setOrganizationId(payload.organization?.id ?? null);
    set({
      user: payload.user,
      organization: payload.organization ?? null,
      organizations: payload.organizations ?? [],
      role: payload.organization?.role ?? payload.role ?? null,
      status: 'authenticated',
    });
  },

  /** Memulihkan sesi saat aplikasi pertama dibuka. */
  async bootstrap() {
    set({ status: 'loading' });
    try {
      const session = await api.refreshSession();
      get().applySession(session);
    } catch {
      // Tidak ada sesi yang bisa dipulihkan - pengguna adalah tamu.
      set({ status: 'unauthenticated', user: null, organization: null, role: null });
    }
  },

  async login(credentials) {
    const { data } = await api.post('/auth/login', credentials);
    get().applySession(data);
    return data;
  },

  async register(payload) {
    const { data } = await api.post('/auth/register', payload);

    // Pendaftaran TIDAK lagi memberi sesi. Server menahannya sampai alamat
    // emailnya dikonfirmasi, jadi tidak ada yang bisa diterapkan di sini.
    // Yang dikembalikan cuma keterangan untuk layar berikutnya.
    return data;
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      // Sesi lokal selalu dibersihkan, sekalipun panggilan server gagal - 
      // pengguna yang menekan "Keluar" harus benar-benar keluar.
      setAccessToken(null);
      setOrganizationId(null);
      set({
        user: null,
        organization: null,
        organizations: [],
        role: null,
        status: 'unauthenticated',
      });
    }
  },

  /** Berpindah ruang kerja tanpa perlu login ulang. */
  switchOrganization(organizationId) {
    const organization = get().organizations.find((o) => o.id === organizationId);
    if (!organization) return;
    setOrganizationId(organizationId);
    set({ organization, role: organization.role });
  },

  updateUser(patch) {
    set((state) => ({ user: { ...state.user, ...patch } }));
  },

  clearSession() {
    setAccessToken(null);
    setOrganizationId(null);
    set({ user: null, organization: null, organizations: [], role: null, status: 'unauthenticated' });
  },
}));

/** Urutan peran, untuk memeriksa hak akses di komponen. */
const ROLE_RANK = { viewer: 1, member: 2, admin: 3, owner: 4 };

export const useCan = (minimumRole) => {
  const role = useAuthStore((state) => state.role);
  return (ROLE_RANK[role] ?? 0) >= (ROLE_RANK[minimumRole] ?? Infinity);
};
/**
 * Peran platform pengguna aktif: 'user', 'admin', 'dev', atau 'root'.
 * Terpisah dari useCan, yang mengukur peran DI DALAM organisasi.
 */
export const usePlatformRole = () => useAuthStore((s) => s.user?.platformRole ?? 'user');

/** Benar bila peran platform pengguna termasuk salah satu yang disebut.
    Root selalu lolos, mengikuti aturan yang sama dengan sisi server. */
export const useIsPlatform = (...roles) => {
  const peran = usePlatformRole();
  return peran === 'root' || roles.includes(peran);
};
